import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import SectionCard from './SectionCard';
import KpiStat from './KpiStat';
import useModuleStats from './useModuleStats';
import RecentItems from './RecentItems';
import { transactionStatusLabels } from './statusLabels';
import CompletionRing from './CompletionRing';
import { formatCount } from '../overview/format';

/**
 * Minutes of Meeting — low-cardinality data, so counts + an action-items
 * completion ring are the right representation.
 */
const MOMSection: React.FC = () => {
  const { isRTL } = useLanguage();
  const { data, loading, error } = useModuleStats<any>('/moms/statistics/');

  if (error) return null;
  // No records yet: keep the module visible with an empty state rather than hiding it.
  const empty = !loading && (!data || (data.total_moms ?? 0) === 0)
    ? {
        title: isRTL ? 'لا توجد محاضر اجتماعات حتى الآن' : 'No minutes of meeting yet',
        text: isRTL ? 'ستظهر هنا إحصائيات المحاضر وبنود العمل بمجرد إنشاء أول محضر.' : 'Minutes and action-item statistics will appear here once the first MOM is created.',
        action: { to: '/moms/new', label: isRTL ? 'إنشاء محضر' : 'Create MOM' },
      }
    : null;

  const totalItems = data?.total_action_items ?? 0;
  const doneItems = data?.completed_action_items ?? 0;
  const pendingItems = data?.pending_action_items ?? 0;
  const overdueItems = data?.overdue_action_items ?? 0;
  const pct = totalItems > 0 ? Math.round((doneItems / totalItems) * 100) : 0;

  return (
    <SectionCard
      icon="bi-journal-text"
      title={isRTL ? 'محاضر الاجتماعات' : 'Minutes of Meeting'}
      caption={isRTL ? 'المحاضر وإنجاز بنود العمل' : 'Minutes and action items'}
      to="/moms"
      loading={loading}
      empty={empty}
      stats={(
        <>
          <KpiStat value={formatCount(data?.total_moms)} label={isRTL ? 'الإجمالي' : 'Total MOMs'} />
          <KpiStat value={formatCount(data?.sent_moms)} label={isRTL ? 'مُرسلة' : 'Sent'} tone="primary" />
          <KpiStat value={formatCount(data?.completed_moms)} label={isRTL ? 'مكتملة' : 'Completed'} tone="success" />
          <KpiStat
            value={formatCount(overdueItems)}
            label={isRTL ? 'بنود متأخرة' : 'Overdue Items'}
            tone={overdueItems > 0 ? 'error' : 'default'}
          />
        </>
      )}
    >
      {totalItems > 0 && (
        <div>
          <div className="dash-subtitle">{isRTL ? 'إنجاز بنود العمل' : 'Action Items Completion'}</div>
          <CompletionRing
            percent={pct}
            label={`${formatCount(doneItems)}/${formatCount(totalItems)}`}
            legend={[
              { key: 'done', label: isRTL ? 'منجزة' : 'Done', value: formatCount(doneItems), color: 'var(--dash-green)' },
              { key: 'pending', label: isRTL ? 'بانتظار الإنجاز' : 'Pending', value: formatCount(pendingItems), color: 'var(--dash-brand)' },
              { key: 'overdue', label: isRTL ? 'متأخرة' : 'Overdue', value: formatCount(overdueItems), color: 'var(--dash-amber)' },
            ]}
          />
          {overdueItems > 0 && (
            <Link to="/moms" className="dash-card-link inline-flex items-center gap-2">
              <i className="bi bi-exclamation-circle" aria-hidden="true"></i>
              {isRTL ? `${overdueItems} بند متأخر — راجع المحاضر` : `${overdueItems} overdue item(s) — review MOMs`}
            </Link>
          )}
        </div>
      )}
      <RecentItems
        endpoint="/moms/"
        title={isRTL ? 'أحدث المحاضر' : 'Recent MOMs'}
        itemLink={(m) => `/moms/${m.id}`}
        primary={(m) => (isRTL && m.title_ar ? m.title_ar : m.title)}
        secondary={(m) => m.mom_id}
        statusKey={(m) => m.status}
        labels={{
          ...transactionStatusLabels(isRTL),
          sent: isRTL ? 'مُرسل' : 'Sent',
          pending_approval: isRTL ? 'بانتظار الاعتماد' : 'Pending Approval',
          rejected: isRTL ? 'مرفوض' : 'Rejected',
        }}
      />
    </SectionCard>
  );
};

export default MOMSection;
